import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

export type ThemeMode = "light" | "dark";

type ThemeModeContextValue = {
  mode: ThemeMode;
  toggle: () => void;
  setMode: (m: ThemeMode) => void;
};

// v2: dark is the site-wide default; a user who never touches the navbar switch
// always lands on dark. The key bump retired the stale "light" preferences left
// over from v1. The switch itself lives in AppNavbar and drives `toggle` below.
const STORAGE_KEY = "cm-theme-mode-v2";

const ThemeModeContext = createContext<ThemeModeContextValue>({
  mode: "dark",
  toggle: () => {},
  setMode: () => {},
});

function readInitialMode(): ThemeMode {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "light" || stored === "dark") return stored;
  } catch {
    /* localStorage unavailable (private mode / SSR) — fall through */
  }
  return "dark";
}

export function ThemeModeProvider({ children }: React.PropsWithChildren) {
  const [mode, setModeState] = useState<ThemeMode>(readInitialMode);

  // Reflect the mode on <html data-theme> so plain-CSS surfaces can react via
  // the `[data-theme="dark"]` token overrides, and persist the choice.
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", mode);
    try {
      localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      /* ignore persistence failures */
    }
  }, [mode]);

  const setMode = useCallback((m: ThemeMode) => setModeState(m), []);
  const toggle = useCallback(
    () => setModeState((m) => (m === "dark" ? "light" : "dark")),
    [],
  );

  const value = useMemo(() => ({ mode, toggle, setMode }), [mode, toggle, setMode]);

  return (
    <ThemeModeContext.Provider value={value}>
      {children}
    </ThemeModeContext.Provider>
  );
}

export function useThemeMode() {
  return useContext(ThemeModeContext);
}
